// src/paginas/secretaria/sre/SREsPage.js

import React, { useEffect, useRef, useState } from "react";

import {
  iconesGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
  tamanhosModaisGlobais,
} from "../../../estilos/global";

import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AddCircleRounded,
  ArrowDownward,
  ArrowUpward,
  CheckCircleRounded,
  Delete,
  Edit,
  EmailRounded,
  LinkOutlined,
  PhoneOutlined,
  PhoneRounded,
  Place,
  Search,
  SettingsRounded,
  UnfoldMore,
  WarningAmberRounded,
} from "@mui/icons-material";

import { useLocation } from "react-router-dom";

import ErrorState from "@componentes/ErrorWindow";
import { api } from "@api/api";

import CriarSRE from "./CriarSRE";
import FormularioSre from "./FormularioSre";
import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";
import { BuildingsFillIcon } from "../../../componentes/ItemList";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

// AÇÕES

const ESTILO_ACAO_EDITAR = {
  width: 30,
  height: 30,
  p: 0,
  color: "#087cf0",
  backgroundColor: "#eaf4ff",
  border: "1px solid #d7eaff",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",

  "&:hover": {
    color: "#0767d8",
    backgroundColor: "#dceeff",
    transform: "translateY(-1px)",
  },

  "&.Mui-disabled": {
    color: "#9fb7ce",
    backgroundColor: "#f3f7fa",
    borderColor: "#e0e8ef",
  },
};

const ESTILO_ACAO_EXCLUIR = {
  width: 30,
  height: 30,
  p: 0,
  color: "#e53935",
  backgroundColor: "#ffeded",
  border: "1px solid #ffdcdc",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",

  "&:hover": {
    color: "#c62828",
    backgroundColor: "#ffdddd",
    transform: "translateY(-1px)",
  },

  "&.Mui-disabled": {
    color: "#c9a5a5",
    backgroundColor: "#faf3f3",
    borderColor: "#eee2e2",
  },
};

// TELEFONE / SITE

function formatarTelefone(valor) {
  const numeros = String(valor || "")
    .replace(/\D/g, "")
    .slice(0, 11);

  if (!numeros) {
    return "";
  }

  if (numeros.length <= 2) {
    return `(${numeros}`;
  }

  const local = numeros.slice(2);
  const corte = numeros.length > 10 ? 5 : 4;

  return (
    `(${numeros.slice(0, 2)}) ${local.slice(0, corte)}` +
    (local.length > corte ? `-${local.slice(corte)}` : "")
  );
}

function separarTelefones(valor) {
  return String(valor || "")
    .split(/\s*\/\s*|\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatarTelefoneExistente(valor) {
  return /^\d{10,11}$/.test(String(valor || "").replace(/\D/g, ""))
    ? formatarTelefone(valor)
    : valor;
}

function obterSite(sre) {
  const partes = String(sre.address || "").split(/\s*\|\s*Site:\s*/i);

  return (sre.site || partes[1] || "").trim();
}

function SiteSre({ sre }) {
  const site = obterSite(sre);

  if (!site) {
    return "—";
  }

  const href = /^https?:\/\//i.test(site)
    ? site
    : /^www\./i.test(site)
      ? `https://${site}`
      : null;

  if (!href) {
    return (
      <Typography
        component="span"
        sx={{
          color: "#40597c",
          fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
        }}
      >
        {site}
      </Typography>
    );
  }

  return (
    <Tooltip title={site}>
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.55,
          maxWidth: "100%",
          color: "#0767d8",
          fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
          textDecoration: "none",

          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        <LinkOutlined
          sx={{
            fontSize: iconesGlobais.conteudoTabela.fontSize,
            flexShrink: 0,
          }}
        />

        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {site.replace(/^https?:\/\//i, "")}
        </Box>
      </Box>
    </Tooltip>
  );
}

// PÁGINA

export default function SREsPage() {
  const { search } = useLocation();

  const previewVazio =
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(search).get("previewVazio") === "1";

  // LISTA

  const [sres, setSres] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);
  const [busca, setBusca] = useState("");

  const [ordem, setOrdem] = useState({
    coluna: null,
    direcao: "asc",
  });

  const [carregando, setCarregando] = useState(true);
  const [erroLista, setErroLista] = useState(null);
  const [recarregar, setRecarregar] = useState(0);

  // MODAIS / OPERAÇÕES

  const [criacaoAberta, setCriacaoAberta] = useState(false);
  const [edicaoId, setEdicaoId] = useState(null);
  const [sreVisualizada, setSreVisualizada] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [erroOperacao, setErroOperacao] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [sreParaExcluir, setSreParaExcluir] = useState(null);

  const [confirmacaoExclusaoAberta, setConfirmacaoExclusaoAberta] =
    useState(false);

  const ignorarExclusaoAte = useRef(0);

  // CARREGAMENTO

  useEffect(() => {
    if (previewVazio) {
      setCarregando(false);
      setErroLista(null);
      return;
    }

    let ativo = true;

    const carregar = async () => {
      try {
        setCarregando(true);
        setErroLista(null);

        const response = await fetch(api.listSres(), {
          credentials: "include",
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || "Erro ao buscar SREs.");
        }

        if (ativo) {
          setSres(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!ativo) {
          return;
        }

        console.error("Erro ao listar SREs:", error);

        if (error instanceof TypeError) {
          setErroLista("network");
        } else {
          setErroLista("module");
        }
      } finally {
        if (ativo) {
          setCarregando(false);
        }
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [recarregar, previewVazio]);

  const atualizarLista = () => setRecarregar((atual) => atual + 1);

  // FILTRO / ORDENAÇÃO

  const comparar = new Intl.Collator("pt-BR", {
    sensitivity: "base",
    numeric: true,
  });

  const filtradas = (previewVazio ? [] : sres)
    .filter((sre) =>
      (sre.name || "")
        .toLocaleLowerCase("pt-BR")
        .includes(busca.trim().toLocaleLowerCase("pt-BR")),
    )
    .sort((a, b) => {
      if (!ordem.coluna) {
        return 0;
      }

      let resultado;

      if (ordem.coluna === "cities") {
        resultado = (a.cities?.length || 0) - (b.cities?.length || 0);
      } else {
        const valorA =
          ordem.coluna === "site" ? obterSite(a) : a[ordem.coluna] || "";

        const valorB =
          ordem.coluna === "site" ? obterSite(b) : b[ordem.coluna] || "";

        resultado = comparar.compare(valorA, valorB);
      }

      if (resultado === 0) {
        resultado = (a.id || 0) - (b.id || 0);
      }

      return ordem.direcao === "asc" ? resultado : -resultado;
    });

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPaginaAtual(1);
  };

  // PAGINAÇÃO

  const totalPaginas = Math.max(
    1,
    Math.ceil(filtradas.length / itensPorPagina),
  );

  const paginaSegura = Math.min(paginaAtual, totalPaginas);

  useEffect(() => {
    setPaginaAtual((atual) => Math.min(atual, totalPaginas));
  }, [totalPaginas]);

  const inicio = (paginaSegura - 1) * itensPorPagina;
  const fim = Math.min(inicio + itensPorPagina, filtradas.length);
  const exibidas = filtradas.slice(inicio, fim);

  const resumoResultados = filtradas.length
    ? `Mostrando ${inicio + 1} a ${fim} de ${filtradas.length} resultados`
    : "Mostrando 0 resultados";

  // EXCLUSÃO

  const excluir = async (id) => {
    if (
      feedback ||
      Date.now() < ignorarExclusaoAte.current ||
      deletingId !== null
    ) {
      return;
    }

    setErroOperacao("");
    setDeletingId(id);

    try {
      const response = await fetch(api.deleteSre(id), {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 409) {
        setErroOperacao(
          typeof data.message === "string"
            ? data.message
            : "Esta SRE possui cadastros vinculados e não pode ser excluída.",
        );

        return;
      }

      if (!response.ok) {
        const mensagem = Array.isArray(data.message)
          ? data.message.join(" ")
          : data.message;

        throw new Error(mensagem || "Não foi possível excluir a SRE.");
      }

      setFeedback({
        action: "delete",
        title: "SRE excluída com sucesso",
        description:
          "A SRE foi removida. Os municípios cadastrados permanecem disponíveis.",
      });
    } catch (error) {
      console.error("Erro ao excluir SRE:", error);

      setErroOperacao(
        error instanceof TypeError
          ? "Não foi possível conectar à API. Verifique se o backend está ativo."
          : error.message || "Não foi possível concluir a exclusão.",
      );
    } finally {
      setDeletingId(null);
      setConfirmacaoExclusaoAberta(false);
    }
  };

  const fecharFeedback = (event) => {
    event?.stopPropagation?.();

    if (!feedback) {
      return;
    }

    ignorarExclusaoAte.current = Date.now() + 800;

    setFeedback(null);
    atualizarLista();
  };

  // CARREGAMENTO / ERRO

  if (!previewVazio && carregando && sres.length === 0) {
    return (
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!previewVazio && erroLista && sres.length === 0) {
    return <ErrorState errorType={erroLista} onRetry={atualizarLista} />;
  }

  // COLUNAS

  const colunas = [
    {
      chave: "name",
      titulo: "Superintendência",
      largura: "20%",
      Icone: BuildingsFillIcon,
    },
    {
      chave: "email",
      titulo: "E-mail Institucional",
      largura: "24%",
      Icone: EmailRounded,
    },
    {
      chave: "phone",
      titulo: "Telefone",
      largura: "16%",
      Icone: PhoneRounded,
    },
    {
      chave: "site",
      titulo: "Site",
      largura: "14%",
      Icone: LinkOutlined,
    },
    {
      chave: "cities",
      titulo: "Cidades",
      largura: "14%",
      Icone: Place,
    },
  ];

  const renderCabecalhoOrdenavel = (coluna) => {
    const ativo = ordem.coluna === coluna.chave;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna.chave)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          color: "#27456f",
          fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
          fontWeight: 700,

          "&:hover": {
            color: "#0767d8",
          },

          "&.Mui-active": {
            color: "#27456f",
          },

          "& .MuiTableSortLabel-icon": {
            opacity: 1,
            color: "#385b90",
            fontSize: iconesGlobais.conteudoTabela.fontSize,
            ml: 0.35,
            transform: "none !important",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.65,
            minWidth: 0,
          }}
        >
          <coluna.Icone
            sx={{
              color: "#087cf0",
              fontSize: iconesGlobais.tabela.fontSize,
              flexShrink: 0,
            }}
          />

          <Box
            component="span"
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            {coluna.titulo}
          </Box>
        </Box>
      </TableSortLabel>
    );
  };

  return (
    <Box
      component={TransicaoPagina}
      variante="navegacao"
      sx={{
        width: "100%",
        py: 0,
      }}
    >
      {/* CABEÇALHO */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: 1.5,
              color: "#087cf0",
              backgroundColor: "#e8f4ff",
            }}
          >
            <BuildingsFillIcon
              sx={{
                fontSize: 27,
              }}
            />
          </Box>

          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: "#0767d8",
                fontSize: tamanhosPaginasAdministrativas.tituloPagina,
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              Superintendências Regionais de Ensino
            </Typography>

            <Typography
              sx={{
                mt: 0,
                color: "#7b8798",
                fontSize: tamanhosControlesGlobais.descricao,
                lineHeight: 1.35,
              }}
            >
              Gerencie as SREs e os municípios vinculados a cada regional.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={
            <AddCircleRounded
              sx={{
                fontSize: "18px !important",
              }}
            />
          }
          onClick={() => setCriacaoAberta(true)}
          sx={{
            minHeight: 34,
            px: 2,
            borderRadius: 1,
            textTransform: "none",
            whiteSpace: "nowrap",
            fontSize: tamanhosControlesGlobais.botao,
            fontWeight: 500,
            boxShadow: "0 2px 6px rgba(7, 103, 216, 0.16)",
            background: "linear-gradient(90deg, #0656ed, #0795f5)",

            "&:hover": {
              background: "linear-gradient(90deg, #064bd4, #0786df)",
              boxShadow: "0 3px 8px rgba(7, 103, 216, 0.2)",
            },
          }}
        >
          Adicionar SRE
        </Button>
      </Box>

      {/* BUSCA / REGISTROS POR PÁGINA */}

      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 1.25,
          px: 1.25,
          py: 0.55,
          mb: 0.5,
          border: "1px solid #d5e7f7",
          borderRadius: 1.5,
          backgroundColor: "#f7fbff",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar SRE..."
            inputProps={{
              "aria-label": "Buscar SRE",
            }}
            value={busca}
            onChange={(event) => {
              setBusca(event.target.value);
              setPaginaAtual(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      color: "#087cf0",
                      fontSize: 18,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                md: 440,
              },
              maxWidth: "100%",

              "& .MuiOutlinedInput-root": {
                height: 34,
                backgroundColor: "#ffffff",
                borderRadius: 1,

                "& fieldset": {
                  borderColor: "#d7e0ea",
                },

                "&:hover fieldset": {
                  borderColor: "#a9c9e8",
                },
              },

              "& .MuiInputBase-input": {
                fontSize: tamanhosControlesGlobais.campoBusca,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
            width: "1px",
            height: 30,
            flexShrink: 0,
            backgroundColor: "#d8e7f4",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: {
              xs: "space-between",
              md: "flex-end",
            },
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#667b98",
              fontSize: tamanhosControlesGlobais.rotulo,
              whiteSpace: "nowrap",
            }}
          >
            Registros por página:
          </Typography>

          <Select
            size="small"
            value={itensPorPagina}
            onChange={(event) => {
              setItensPorPagina(Number(event.target.value));
              setPaginaAtual(1);
            }}
            renderValue={(valor) => `${valor} por página`}
            sx={{
              width: 160,
              height: 34,
              backgroundColor: "#ffffff",
              color: "#314d76",
              borderRadius: 1,
              fontSize: tamanhosControlesGlobais.seletor,
              fontWeight: 500,

              "& .MuiSelect-select": {
                py: 0.55,
              },
            }}
          >
            {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
              <MenuItem key={quantidade} value={quantidade}>
                {quantidade} por página
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* PAGINAÇÃO SUPERIOR */}

      {filtradas.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            px: 1.5,
            py: 0.35,
            mb: 0.5,
            border: "1px solid #d7e8f8",
            borderRadius: 1.5,
            backgroundColor: "#f2f8fe",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#536b8b",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {filtradas.length}{" "}
            {filtradas.length === 1 ? "registro" : "registros"}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaSegura}
              totalPaginas={totalPaginas}
              onChange={setPaginaAtual}
              ariaLabel="Paginação superior da lista de SREs"
            />
          </Box>
        </Box>
      )}

      {/* TABELA */}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 1.5,
          border: "1px solid #d7e7f7",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 960,
            tableLayout: "fixed",

            "& .MuiTableCell-root": {
              borderColor: "#dfe9f3",
            },
          }}
        >
          <TableHead
            sx={{
              backgroundColor: "#edf6fe",
            }}
          >
            <TableRow>
              {colunas.map((coluna) => (
                <TableCell
                  key={coluna.chave}
                  align="left"
                  sx={{
                    width: coluna.largura,
                    px: 1.4,
                    py: 0.55,
                    borderRight: "1px solid #d9e7f3",
                  }}
                >
                  {renderCabecalhoOrdenavel(coluna)}
                </TableCell>
              ))}

              <TableCell
                align="center"
                sx={{
                  width: "12%",
                  px: 1,
                  py: 0.55,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.65,
                    color: "#27456f",
                  }}
                >
                  <SettingsRounded
                    sx={{
                      color: "#087cf0",
                      fontSize: iconesGlobais.tabela.fontSize,
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#27456f",
                      fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
                      fontWeight: 700,
                    }}
                  >
                    Ações
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {exibidas.length ? (
              exibidas.map((sre) => {
                const telefones = separarTelefones(sre.phone);

                return (
                  <TableRow
                    key={sre.id}
                    hover
                    sx={{
                      height: 38,

                      "&:hover": {
                        backgroundColor: "#fbfdff",
                      },

                      "&:last-child td": {
                        borderBottom: 0,
                      },
                    }}
                  >
                    {/* SUPERINTENDÊNCIA */}

                    <TableCell
                      align="left"
                      sx={{
                        px: 1.4,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      <Tooltip title={sre.name || ""}>
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#0767d8",
                            fontSize:
                              tamanhosPaginasAdministrativas.conteudoTabela,
                            fontWeight: 600,
                          }}
                        >
                          {sre.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* EMAIL */}

                    <TableCell
                      align="left"
                      sx={{
                        px: 1.4,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      <Tooltip title={sre.email || ""}>
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#40597c",
                            fontSize:
                              tamanhosPaginasAdministrativas.conteudoTabela,
                          }}
                        >
                          {sre.email || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* TELEFONE */}

                    <TableCell
                      align="left"
                      sx={{
                        px: 1.4,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      {telefones.length ? (
                        telefones.map((telefone, indice) => (
                          <Box
                            key={indice}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mb: indice ? 0.1 : 0,
                              color: "#40597c",
                              fontSize:
                                tamanhosPaginasAdministrativas.conteudoTabela,
                              lineHeight: 1.15,
                            }}
                          >
                            <PhoneOutlined
                              sx={{
                                fontSize: iconesGlobais.conteudoTabela.fontSize,
                                color: "#64748b",
                                flexShrink: 0,
                              }}
                            />

                            <Box
                              component="span"
                              sx={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatarTelefoneExistente(telefone)}
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography
                          sx={{
                            color: "#64748b",
                            fontSize:
                              tamanhosPaginasAdministrativas.conteudoTabela,
                          }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* SITE */}

                    <TableCell
                      align="left"
                      sx={{
                        px: 1.4,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                        overflow: "hidden",
                      }}
                    >
                      <SiteSre sre={sre} />
                    </TableCell>

                    {/* CIDADES */}

                    <TableCell
                      align="left"
                      sx={{
                        px: 1.2,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          <Place
                            sx={{
                              fontSize: `${iconesGlobais.conteudoTabela.fontSize} !important`,
                            }}
                          />
                        }
                        onClick={() => setSreVisualizada(sre)}
                        sx={{
                          minHeight: 26,
                          px: 1,
                          py: 0.1,
                          borderRadius: 4,
                          borderColor: "#add4fc",
                          color: "#087cf0",
                          fontSize:
                            tamanhosPaginasAdministrativas.conteudoTabela,
                          fontWeight: 500,
                          textTransform: "none",
                          whiteSpace: "nowrap",

                          "&:hover": {
                            bgcolor: "#087cf0",
                            borderColor: "#087cf0",
                            color: "#fff",
                          },
                        }}
                      >
                        Cidades ({sre.cities?.length || 0})
                      </Button>
                    </TableCell>

                    {/* AÇÕES */}

                    <TableCell
                      align="center"
                      sx={{
                        px: 0.5,
                        py: 0.35,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.85,
                        }}
                      >
                        <Tooltip title="Editar SRE">
                          <IconButton
                            size="small"
                            aria-label={`Editar ${sre.name}`}
                            onClick={() => setEdicaoId(sre.id)}
                            sx={ESTILO_ACAO_EDITAR}
                          >
                            <Edit
                              sx={{
                                fontSize: iconesGlobais.tabela.fontSize,
                              }}
                            />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Excluir SRE">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Excluir ${sre.name}`}
                              disabled={
                                Boolean(feedback) || deletingId !== null
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                setSreParaExcluir(sre);
                                setConfirmacaoExclusaoAberta(true);
                              }}
                              sx={ESTILO_ACAO_EXCLUIR}
                            >
                              <Delete
                                sx={{
                                  fontSize: iconesGlobais.tabela.fontSize,
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{
                    py: 5,
                    color: "text.secondary",
                    fontSize: tamanhosControlesGlobais.descricao,
                  }}
                >
                  {busca.trim()
                    ? "Nenhuma SRE corresponde à busca."
                    : "Ainda não há SREs cadastradas."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINAÇÃO INFERIOR */}

      {filtradas.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            mt: 0.45,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#526b8d",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {resumoResultados}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaSegura}
              totalPaginas={totalPaginas}
              onChange={setPaginaAtual}
              ariaLabel="Paginação inferior da lista de SREs"
            />
          </Box>
        </Box>
      )}

      {/* CIDADES VINCULADAS */}

      <Dialog
        open={Boolean(sreVisualizada)}
        onClose={() => setSreVisualizada(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="cidades-sre-titulo"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: "5px solid #087cf0",

            width: {
              xs: "calc(100% - 32px)",
              sm: 680,
            },

            maxWidth: 680,
          },
        }}
      >
        <DialogTitle
          id="cidades-sre-titulo"
          sx={{
            color: "#087cf0",
            fontSize: tamanhosModaisGlobais.tituloModal,
            fontWeight: 700,
            px: 3,
            pt: 2.5,
            pb: 1.5,
            pr: 6,
          }}
        >
          Cidades vinculadas
          <IconButton
            aria-label="Fechar cidades da SRE"
            onClick={() => setSreVisualizada(null)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "#314d76",

              "&:hover": {
                color: "#087cf0",
                bgcolor: "#e0f0ff",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 24,
                lineHeight: 1,
                fontWeight: 300,
              }}
            >
              ×
            </Box>
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            pt: "4px !important",
            pb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              pb: 2,
              mb: 2,
              borderBottom: "1px solid #e7edf5",
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: "#e0f0ff",
                color: "#087cf0",
              }}
            >
              <Place
                sx={{
                  fontSize: 27,
                }}
              />
            </Avatar>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#123d78",
                  fontWeight: 700,
                  fontSize: tamanhosModaisGlobais.tituloSecao,
                  lineHeight: 1.3,
                }}
              >
                {sreVisualizada?.name}
              </Typography>

              <Typography
                sx={{
                  color: "#60748f",
                  fontSize: tamanhosModaisGlobais.descricao,
                }}
              >
                Municípios vinculados a esta regional.
              </Typography>
            </Box>
          </Box>

          {sreVisualizada?.cities?.length ? (
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },

                gap: 1.25,
                maxHeight: "min(58vh, 480px)",
                overflowY: "auto",
              }}
            >
              {sreVisualizada.cities.map((cidade) => (
                <Box
                  key={cidade.id}
                  sx={{
                    bgcolor: "#f1f6fc",
                    borderRadius: 1.5,
                    minHeight: 46,
                    px: 2,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <Place
                    sx={{
                      fontSize: 19,
                      color: "#087cf0",
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: tamanhosModaisGlobais.campo,
                      color: "#233a5d",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {cidade.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              sx={{
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
              }}
            >
              Nenhuma cidade vinculada.
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e7edf5",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setSreVisualizada(null)}
            sx={{
              textTransform: "none",
              fontSize: tamanhosModaisGlobais.botao,
              minWidth: 100,

              "&:hover": {
                bgcolor: "#087cf0",
                borderColor: "#087cf0",
                color: "#fff",
              },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* EXCLUSÃO */}

      <Dialog
        open={confirmacaoExclusaoAberta}
        onClose={() => {
          if (deletingId === null) {
            setConfirmacaoExclusaoAberta(false);
          }
        }}
        slotProps={{
          transition: {
            onExited: () => setSreParaExcluir(null),
          },
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="confirmar-exclusao-sre-titulo"
        aria-describedby="confirmar-exclusao-sre-descricao"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: "5px solid #d32f2f",
          },
        }}
      >
        <DialogTitle
          id="confirmar-exclusao-sre-titulo"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#b42318",
            fontSize: tamanhosModaisGlobais.tituloModal,
            fontWeight: 700,
          }}
        >
          <WarningAmberRounded
            sx={{
              fontSize: 28,
            }}
          />
          Excluir SRE?
        </DialogTitle>

        <DialogContent>
          <Typography
            id="confirmar-exclusao-sre-descricao"
            sx={{
              color: "#334155",
              fontSize: tamanhosModaisGlobais.descricao,
            }}
          >
            Tem certeza de que deseja excluir{" "}
            <strong>{sreParaExcluir?.name}</strong>? Essa ação não pode ser
            desfeita.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            disabled={deletingId !== null}
            onClick={() => setConfirmacaoExclusaoAberta(false)}
            sx={{
              textTransform: "none",
              fontSize: tamanhosModaisGlobais.botao,
            }}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            variant="contained"
            color="error"
            disabled={deletingId !== null}
            onClick={() => {
              if (sreParaExcluir) {
                excluir(sreParaExcluir.id);
              }
            }}
            sx={{
              textTransform: "none",
              fontSize: tamanhosModaisGlobais.botao,
            }}
          >
            {deletingId !== null ? "Excluindo..." : "Excluir SRE"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ERRO DA OPERAÇÃO */}

      <Snackbar
        open={Boolean(erroOperacao)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        onClose={(_, reason) => {
          if (reason !== "clickaway") {
            setErroOperacao("");
          }
        }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErroOperacao("")}
          sx={{
            width: "100%",
          }}
        >
          {erroOperacao}
        </Alert>
      </Snackbar>

      {/* FEEDBACK */}

      <Dialog
        open={Boolean(feedback)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="sre-feedback-title"
        PaperProps={{
          sx: {
            border: "1px solid #c9def4",
            borderRadius: 4,
            boxShadow: "0 18px 44px rgba(20,61,106,0.13)",
          },
        }}
      >
        <DialogContent
          sx={{
            pt: 3.5,
            px: 3,
            pb: 1,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 66,
              height: 66,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "#e8f7ef",
              color: "#198452",
            }}
          >
            <CheckCircleRounded
              sx={{
                fontSize: 38,
              }}
            />
          </Box>

          <Typography
            id="sre-feedback-title"
            component="h2"
            sx={{
              color: "#0767d8",
              fontSize: tamanhosModaisGlobais.tituloModal,
              fontWeight: 700,
            }}
          >
            {feedback?.title}
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: tamanhosModaisGlobais.descricao,
              mt: 1,
            }}
          >
            {feedback?.description}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            pt: 1.5,
            pb: 3,
          }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={fecharFeedback}
            sx={{
              bgcolor: "#087cf0",
              borderRadius: 2,
              px: 3.5,
              textTransform: "none",
              fontSize: tamanhosModaisGlobais.botao,

              "&:hover": {
                bgcolor: "#0767d8",
              },
            }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      {/* CRIAR */}

      <CriarSRE
        open={criacaoAberta}
        onClose={() => setCriacaoAberta(false)}
        onCreated={() => {
          setCriacaoAberta(false);
          setErroOperacao("");

          setFeedback({
            action: "create",
            title: "SRE criada com sucesso",
            description: "A nova SRE foi incluída na lista.",
          });
        }}
      />

      {/* EDITAR — Mesmo FormularioSre utilizado pelo cadastro. */}

      {edicaoId !== null && (
        <FormularioSre
          id={edicaoId}
          open
          onClose={() => setEdicaoId(null)}
          onSaved={() => {
            setEdicaoId(null);
            setErroOperacao("");

            setFeedback({
              action: "update",
              title: "SRE atualizada com sucesso",
              description: "Os dados da SRE foram atualizados.",
            });
          }}
        />
      )}
    </Box>
  );
}
